import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../ui/use-toast';
import { Plus, Edit, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { getDishes, updateDish } from '../../services/menuService';
import { SIZE_OPTIONS, UNIT_OPTIONS, getSizeLabel, getUnitQuantityMeta, formatUnitLabel } from '../../constants/dishes';
import { useAppSettings } from '../../context/AppSettingsContext';

function DishesList({ restaurantId, dishType = 'food' }) {
  const navigate = useNavigate();
  const { formatCurrency } = useAppSettings();
  const defaultUnit = dishType === 'beverage' ? 'ml' : 'plate';
  const defaultQuantity = dishType === 'beverage' ? '180' : '1';
  const [dishes, setDishes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showVariantDialog, setShowVariantDialog] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const selectedDishName = selectedDish?.name || '';
  const { toast } = useToast();

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [useSearch, setUseSearch] = useState(false);

  const [variants, setVariants] = useState([
    {
      size: 'full',
      quantity: defaultQuantity,
      unit: defaultUnit,
      variant_name: getSizeLabel('full'),
      variant_name_source: 'size:full',
      price: '',
      calories: '',
      preparation_time: 15,
      spicy_level: 0,
      is_available: true,
      display_order: 0,
    }
  ]);
  const updateVariantField = (index, field, value) => {
    setVariants((prev) => {
      const updated = [...prev];
      const variant = { ...updated[index] };
      let nextValue = value;
      if (field === 'size' && !nextValue) {
        nextValue = 'full';
      }
      if (field === 'unit' && !nextValue) {
        nextValue = defaultUnit;
      }
      variant[field] = nextValue;

      if (field === 'size' && variant.variant_name_source?.startsWith('size:')) {
        variant.variant_name_source = `size:${nextValue}`;
        variant.variant_name = getSizeLabel(nextValue);
      }

      if (field === 'unit' && !variant.quantity) {
        variant.quantity = defaultQuantity;
      }

      updated[index] = variant;
      return updated;
    });
  };

  const handleVariantNameSourceChange = (index, source, dishName) => {
    setVariants((prev) => {
      const updated = [...prev];
      const variant = { ...updated[index] };

      if (source === 'dish') {
        variant.variant_name_source = 'dish';
        variant.variant_name = dishName || '';
      } else if (source === 'custom') {
        variant.variant_name_source = 'custom';
      } else if (source.startsWith('size:')) {
        const sizeValue = source.split(':')[1] || 'full';
        variant.size = sizeValue;
        variant.variant_name_source = `size:${sizeValue}`;
        variant.variant_name = getSizeLabel(sizeValue);
      }

      updated[index] = variant;
      return updated;
    });
  };

  useEffect(() => {
    setCategories([]);
    loadDishes();
    // eslint-disable-next-line
  }, [restaurantId, dishType]);

  const loadDishes = async () => {
    if (!restaurantId) {
      console.warn('DishesList: restaurantId is not set');
      return;
    }
    console.log('DishesList: Loading dishes for restaurant:', restaurantId, 'dishType:', dishType);
    setIsLoading(true);
    try {
      const data = await getDishes(restaurantId);
      const dishArray = Array.isArray(data) ? data : [];
      console.log('DishesList: Loaded', dishArray.length, 'dishes');
      const filteredDishes = dishArray.filter((dish) => dish.dish_type === dishType);
      console.log('DishesList: Filtered to', filteredDishes.length, 'dishes of type', dishType);
      setDishes(filteredDishes);
      setUseSearch(false);
    } catch (error) {
      console.error('DishesList: Error loading dishes:', error);
      setDishes([]);
      if (error.error && error.error !== 'Restaurant not found') {
        toast({
          title: 'Error',
          description: error.error || 'Failed to load dishes',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const performSearch = async (search = searchTerm, category = selectedCategory, page = 1) => {
    setIsLoading(true);
    try {
      // Get all dishes and filter on frontend
      const allDishes = await getDishes(restaurantId);
      let filtered = Array.isArray(allDishes) ? allDishes : [];
      
      // Apply search filter
      if (search && search.trim()) {
        filtered = filtered.filter(dish => 
          dish.name.toLowerCase().includes(search.toLowerCase())
        );
      }
      
      // Apply category filter
      if (category) {
        filtered = filtered.filter(dish => dish.category === parseInt(category));
      }
      
      setDishes(filtered);
      setTotalCount(filtered.length);
      setTotalPages(Math.ceil(filtered.length / 10));
      setCurrentPage(page);
      setUseSearch(true);
    } catch (error) {
      console.error('Error searching dishes:', error);
      toast({
        title: 'Error',
        description: 'Failed to search dishes',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    performSearch(searchTerm, selectedCategory, 1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setCurrentPage(1);
    loadDishes();
  };

  const handlePageChange = (newPage) => {
    performSearch(searchTerm, selectedCategory, newPage);
  };

  useEffect(() => {
    if (!selectedDishName) return;
    setVariants((prev) =>
      prev.map((variant) =>
        variant.variant_name_source === 'dish'
          ? { ...variant, variant_name: selectedDishName }
          : variant
      )
    );
  }, [selectedDishName]);



  const handleSubmitVariant = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const variant = variants[0];
      if (!selectedDish || !variant) {
        throw new Error('Missing dish or variant information');
      }

      const parsedQuantity = parseFloat(variant.quantity);
      const parsedPrice = parseFloat(variant.price);

      if (
        !variant.variant_name?.trim() ||
        !parsedQuantity ||
        parsedQuantity <= 0 ||
        !parsedPrice ||
        parsedPrice <= 0
      ) {
        toast({
          title: 'Variant details missing',
          description: 'Provide a label, quantity/volume, and price for the variant.',
          variant: 'destructive',
        });
        return;
      }

      const payload = {
        size: variant.size || 'full',
        quantity: parsedQuantity,
        unit: variant.unit || defaultUnit,
        variant_name: variant.variant_name.trim(),
        price: parsedPrice,
        calories: variant.calories === '' ? 0 : parseInt(variant.calories, 10),
        spicy_level: parseInt(variant.spicy_level, 10) || 0,
        preparation_time: parseInt(variant.preparation_time, 10) || 15,
        is_available: variant.is_available ?? true,
      };

      // Add variant by updating the dish with the new variant data
      await updateDish(restaurantId, selectedDish.id, { variants: [payload] });
      toast({
        title: 'Success',
        description: 'Variant added successfully',
      });
      setShowVariantDialog(false);
      setSelectedDish(null);
      setVariants([
        {
          size: 'full',
          quantity: defaultQuantity,
          unit: defaultUnit,
          variant_name: getSizeLabel('full'),
          variant_name_source: 'size:full',
          price: '',
          calories: '',
          preparation_time: 15,
          spicy_level: 0,
          is_available: true,
          display_order: 0,
        }
      ]);
      loadDishes();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.error || 'Failed to add variant',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditDish = (dish) => {
    navigate(`/restaurant/${restaurantId}/dish/${dish.id}/edit`, { 
      state: { dish, dishType }
    });
  };

  const handleAddDish = () => {
    navigate(`/restaurant/${restaurantId}/dish/new`, { 
      state: { dishType }
    });
  };

  const handleAddVariant = (dish) => {
    setSelectedDish(dish);
    setVariants([
      {
        size: 'full',
        quantity: defaultQuantity,
        unit: defaultUnit,
        variant_name: dish?.name || getSizeLabel('full'),
        variant_name_source: dish?.name ? 'dish' : 'size:full',
        price: '',
        calories: '',
        preparation_time: 15,
        spicy_level: 0,
        is_available: true,
        display_order: 0,
      }
    ]);
    setShowVariantDialog(true);
  };

  const variantForm = variants[0] || {};
  const nameSelectValue = variantForm.variant_name_source || 'custom';
  const unitMeta = getUnitQuantityMeta(variantForm.unit);

  return (
    <div>
      {/* Search and Filter Section */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Search & Filter</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search Input */}
              <div className="space-y-2">
                <Label htmlFor="search">Search by Name</Label>
                <Input
                  id="search"
                  placeholder="e.g., Butter Chicken"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <Label htmlFor="category">Filter by Category</Label>
                <Select value={selectedCategory || 'all'} onValueChange={(val) => setSelectedCategory(val === 'all' ? '' : val)}>
                  <SelectTrigger id="category">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Page Size */}
              <div className="space-y-2">
                <Label htmlFor="pagesize">Items per Page</Label>
                <Select value={pageSize.toString()} onValueChange={(val) => {
                  setPageSize(parseInt(val));
                  setCurrentPage(1);
                }}>
                  <SelectTrigger id="pagesize">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 items</SelectItem>
                    <SelectItem value="10">10 items</SelectItem>
                    <SelectItem value="20">20 items</SelectItem>
                    <SelectItem value="50">50 items</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit">
                <Search className="h-4 w-4 mr-2" />
                Search
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClearSearch}
              >
                Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Main Dishes Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{dishType === 'food' ? 'Dishes' : 'Beverages'}</CardTitle>
            <CardDescription>
              {useSearch
                ? `Showing ${dishes.length} of ${totalCount} results`
                : `Manage all ${dishType === 'food' ? 'food items' : 'beverages'} with their variants and pricing`}
            </CardDescription>
          </div>
          <Button onClick={handleAddDish}>
            <Plus className="h-4 w-4 mr-2" />
            Add {dishType === 'food' ? 'Dish' : 'Beverage'}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : dishes.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="text-lg font-medium">No {dishType === 'food' ? 'dishes' : 'beverages'} yet</p>
              <p className="text-sm">Add your first {dishType === 'food' ? 'dish' : 'beverage'} to get started</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {dishes.map((dish) => (
                <Card key={dish.id} className="border-2">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold">{dish.name}</h3>
                          {dish.category_name && (
                            <span className="text-xs bg-secondary px-2 py-1 rounded">
                              {dish.category_name}
                            </span>
                          )}
                          {dish.is_vegetarian && !dish.is_vegan && (
                            <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                              Veg
                            </span>
                          )}
                          {dish.is_vegan && (
                            <span className="text-xs bg-emerald-100 text-emerald-900 px-2 py-1 rounded">
                              Vegan
                            </span>
                          )}
                        </div>
                        {dish.description && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {dish.description}
                          </p>
                        )}
                        {dish.variants && dish.variants.length > 0 && (
                          <div className="mt-3 space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">Variants:</p>
                            {dish.variants.map((variant) => (
                              <div
                                key={variant.id}
                                className="flex items-center justify-between bg-muted p-2 rounded text-sm"
                              >
                                <span>
                                  {variant.variant_name}
                                  {variant.quantity ? ` • ${variant.quantity} ${formatUnitLabel(variant.unit)}` : ''}
                                </span>
                                <span className="font-medium">{formatCurrency(variant.price)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddVariant(dish)}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditDish(dish)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pagination Controls */}
          {useSearch && totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1 || isLoading}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages || isLoading}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Variant Dialog - Keep for adding variants to existing dishes */}
      <Dialog open={showVariantDialog} onOpenChange={setShowVariantDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Variant for {selectedDish?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitVariant}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Display Name *</Label>
                <Select
                  value={nameSelectValue}
                  onValueChange={(value) => handleVariantNameSourceChange(0, value, selectedDishName)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose label" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dish" disabled={!selectedDishName}>
                      {selectedDishName ? `Dish Name (${selectedDishName})` : 'Dish Name'}
                    </SelectItem>
                    {SIZE_OPTIONS.map((option) => (
                      <SelectItem key={`dialog-name-${option.value}`} value={`size:${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {nameSelectValue === 'custom' && (
                  <Input
                    value={variantForm.variant_name || ''}
                    onChange={(e) => updateVariantField(0, 'variant_name', e.target.value)}
                    placeholder="e.g., Family Pack"
                    required
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="size">Size</Label>
                  <Select
                    value={variantForm.size || 'full'}
                    onValueChange={(value) => updateVariantField(0, 'size', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SIZE_OPTIONS.map((option) => (
                        <SelectItem key={`dialog-size-${option.value}`} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="unit">Unit</Label>
                  <Select
                    value={variantForm.unit || defaultUnit}
                    onValueChange={(value) => updateVariantField(0, 'unit', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map((option) => (
                        <SelectItem key={`dialog-unit-${option.value}`} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>{unitMeta.label} *</Label>
                <Input
                  type="number"
                  min="0"
                  step={unitMeta.step || 'any'}
                  value={variantForm.quantity || ''}
                  onChange={(e) => updateVariantField(0, 'quantity', e.target.value)}
                  placeholder={unitMeta.placeholder}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="price">Price *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={variantForm.price || ''}
                  onChange={(e) => updateVariantField(0, 'price', e.target.value)}
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowVariantDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Adding...' : 'Add Variant'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default DishesList;
